import { injectable } from 'inversify';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { ResultData } from '../../../api/ApiTypes';
import { RequestTerminal } from '../../../api/RequestTerminal';
import { JiraSprintData, JiraSprintPatchInput } from '../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { PodRecordSet, RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { ActiveObject, resolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { tasksLoadInitial } from '../assignment/TaskCommands';
import { BindingManager } from '../atlassian/BindingManager';
import { PodRecordBindingTarget } from '../atlassian/PodRecordBindingTarget';
import { JiraSprint, JiraSprintDataRecordAttr } from './JiraSprint';
import { jiraSprintsLoad } from './JiraSprintCommands';

const createPatchTerminalSubmit = () =>
    async (api: ApiClient, items: JiraSprintPatchInput[]): Promise<ResultData> => {
        return api.exec(v2.jira.sprints.patch(items));
    };

const createLoadTerminalSubmit = (board: BoardInfo) =>
    async (api: ApiClient, items: string[]): Promise<ResultData> => {
        // Dedupe
        items = Array.from(new Set(items));
        return api.exec(v2.jira.sprints.load({ boardId: board.id, jiraSprintIds: items }));
    };

@injectable()
export class JiraSprintRecordManager extends ActiveObject {

    @resolve(ApiClient) private api: ApiClient;
    @resolve(BindingManager) private bindings: BindingManager;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(RecordSetManager) private recordSets: RecordSetManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ParticipantManager) private ptpts: ParticipantManager;

    private loadTerminal = null as RequestTerminal<string>;
    private patchTerminal = null as RequestTerminal<JiraSprintPatchInput>;
    private recordSet: PodRecordSet<JiraSprintData>;

    public start(): void {
        this.burden(
            this.loadTerminal = this.api.createTerminal(
                createLoadTerminalSubmit(this.session.get(BoardInfo))
            ),
            this.patchTerminal = this.api.createTerminal(
                createPatchTerminalSubmit()
            ),
            this.objects.on('JiraSprint', 'allocated', ([x]) => this.configureBinding(x.as(JiraSprint))),
            this.objects.on('JiraSprint', 'affected', ([x]) => this.configureBinding(x.as(JiraSprint))),
            this.objects.on('JiraSprint', 'released', ([x]) => this.releaseBinding(x.as(JiraSprint))),
            this.objects.createTrigger('JiraSprint.Load')
                .for(JiraSprint)
                .onChangesTo('jiraSprintId')
                .doForEach(this.loadJiraSprintRecordIfRequired),

        );

        this.recordSet = this.recordSets.resolve('JiraSprints');

        this.dispatch(tasksLoadInitial);
        this.configureInitialBindings();
        this.loadInitial();
    }

    public patch(patch: JiraSprintPatchInput) {

        this.patchTerminal.queueNext(patch);

        const elmts = this.objects.findAll(JiraSprint, x => x.jiraSprintId === patch.jiraSprintId);
        for (const tm of elmts) {
            this.bindings.lookup(tm)?.mutate(patch);
        }
    }

    private loadInitial() {
        // Only if we are the first online
        if (this.ptpts.online().filter(x => !x.self).length === 0) {
            this.dispatch(jiraSprintsLoad);
        }
    }

    private loadJiraSprintRecordIfRequired = (sprint: JiraSprint) => {
        if (!sprint.jiraSprintId) return;
        if (!!JiraSprintDataRecordAttr.get(sprint)?.createdDate) return;
        this.loadTerminal.queueNext(sprint.jiraSprintId);
    };

    private configureInitialBindings() {
        const allSprints = Array.from(this.objects.getAll(JiraSprint));
        for (const sprint of allSprints) {
            this.configureBinding(sprint);
        }
    }

    private configureBinding(sprint: JiraSprint) {
        if (!sprint.jiraSprintId) return; // Can't bind
        const key = `record:${sprint.jiraSprintId}`;
        if (this.bindings.check(sprint, key)) return;
        const record = this.recordSet.getForFuture(sprint.jiraSprintId);
        this.bindings.bind(sprint, new PodRecordBindingTarget(key, record), JiraSprintDataRecordAttr);
    }

    private releaseBinding(sprint: JiraSprint) {
        this.bindings.unbind(sprint);
    }
}
